import {FormEvent, useEffect, useMemo, useState} from "react";
import {Alert, Card, Form} from "react-bootstrap";
import {Link, useParams, useSearchParams} from "react-router";
import {CheckoutApi} from "../../api/CheckoutApi";
import {CheckoutQuote, LICENSEE_MAX_LENGTH, offeredProviders, PaymentProvider, SellableEdition} from "../../api/models";
import {useAuth} from "../../auth/AuthContext";
import {usePublishedProduct} from "../products/usePublishedProduct";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert, fieldError} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {Field, FieldControl, FieldSelect} from "../../components/Field";
import {SubmitButton} from "../../components/SubmitButton";
import {emailProblem, formatPrice} from "../../components/format";
import {allCountries, guessCountry, POSTAL_CODE_COUNTRIES} from "../../components/countries";
import {checkout as messages, format, products, rich} from "../../i18n";
import {useSiteStatsHit} from "../../components/siteStats";
import {TrademarkNotices, trademarksIn} from "../../components/TrademarkNotices";

// Buying a license, signed in or not: a guest gives an email address, and once they've paid the license goes to that
// address's account, or to one made for it (the API emails it a link to choose a password). Payment happens on Stripe's or PayPal's own page, or in Paddle's checkout (on
// /checkout/pay): this page only says what's being bought and how to pay, then sends the customer there. They come back
// to /checkout/complete, or here with ?cancelled=1.
export function CheckoutPage() {
    const {sku = ''} = useParams();
    const [search] = useSearchParams();
    const {user, loading: authLoading} = useAuth();
    const {data: product, error: productError, loading: productLoading} = usePublishedProduct(sku);
    const {data: config, error: configError} = useAsync(() => CheckoutApi.config(), []);
    // None while the shop's closed.
    const providers = useMemo(() => config?.enabled ? offeredProviders(config) : [], [config]);

    const [provider, setProvider] = useState<PaymentProvider>();
    const [licensee, setLicensee] = useState(user?.displayName || user?.username || '');
    const [email, setEmail] = useState('');
    const [country, setCountry] = useState(guessCountry() ?? '');
    const [postalCode, setPostalCode] = useState('');
    const [agreed, setAgreed] = useState(false);
    const [waived, setWaived] = useState(false);
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const price = product && !product.beta ? product.prices.find(x => x.edition === SellableEdition && x.amount > 0) : undefined;
    const chosen = provider ?? providers[0];
    // Counted once the form shows: as a view, or as a return from Stripe or PayPal without paying.
    useSiteStatsHit(product && price && config && providers.length
        ? { event: search.get('cancelled') ? 'checkout-cancelled' : 'checkout-view', sku: product.sku }
        : undefined);
    const needsPostalCode = POSTAL_CODE_COUNTRIES.includes(country);
    const problems = {
        email: !user ? (!email.trim() ? messages.problems.email : emailProblem(email)) : undefined,
        licensee: !licensee.trim() ? messages.problems.licensee : undefined,
        country: chosen === 'paypal' && !country ? messages.problems.country : undefined,
        postalCode: chosen === 'paypal' && needsPostalCode && !postalCode.trim() ? messages.problems.postalCode : undefined,
        agreed: !agreed ? messages.problems.agreed : undefined,
        waived: !waived ? messages.problems.waived : undefined
    };

    // PayPal's total, with the tax for where the customer is (Stripe shows its own, once it has the billing address).
    const [quote, setQuote] = useState<CheckoutQuote>();
    const [quoteError, setQuoteError] = useState<unknown>();
    useEffect(() => {
        setQuote(undefined);
        setQuoteError(undefined);
        if (chosen !== 'paypal' || !price || !country || (needsPostalCode && !postalCode.trim())) {
            return;
        }
        let current = true;
        const timer = setTimeout(() => {
            CheckoutApi.quote(sku, country, postalCode.trim()).then(
                result => { if (current) setQuote(result); },
                err => { if (current) setQuoteError(err); });
        }, 400);
        return () => {
            current = false;
            clearTimeout(timer);
        };
    }, [chosen, sku, country, postalCode, needsPostalCode, !!price]);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (!chosen || Object.values(problems).some(Boolean)) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            const started = await CheckoutApi.start({
                sku,
                provider: chosen,
                licensee: licensee.trim(),
                ...(user ? {} : { email: email.trim() }),
                ...(chosen === 'paypal' ? { country, postalCode: postalCode.trim() } : {})
            });
            // Stays busy: the page is leaving for the provider's.
            window.location.assign(started.redirectUrl);
        } catch (err) {
            setError(err);
            setBusy(false);
        }
    };

    const back = <div className="mb-2"><Link to={`/products/${encodeURIComponent(sku)}`} className="small"><i className="bi bi-arrow-left me-1" aria-hidden="true"></i>{product?.name ?? messages.product}</Link></div>;
    if ((productLoading && !product) || authLoading) {
        return <>{back}<LoadingSpinner/></>;
    }
    if (productError || configError) {
        return <>{back}<PageHeader title={messages.buy}/><ErrorAlert error={productError ?? configError} messages={{ 404: products.noSuchProduct }}/></>;
    }
    if (!product) {
        return null;
    }
    const title = format(messages.title, { product: product.name });
    if (!price || (config && providers.length === 0)) {
        return (
            <>
                {back}
                <PageHeader title={title}/>
                <Alert variant="secondary">{format(messages.notOnSale, { product: product.name })}</Alert>
            </>
        );
    }
    if (!config) {
        return <>{back}<LoadingSpinner/></>;
    }

    return (
        <>
            {back}
            <PageHeader title={title}/>
            {search.get('cancelled') && (
                <Alert variant="info">{messages.cancelled}</Alert>
            )}
            <Form noValidate onSubmit={submit} className="checkout-form">
                <ErrorAlert error={error} shownFields={['email', 'licensee', 'country', 'postalCode']} messages={{
                    not_for_sale: format(messages.notForSale, { product: product.name }),
                    429: messages.tooManyStarted
                }}/>
                <Card className="mb-4">
                    <Card.Body className="d-flex flex-wrap justify-content-between gap-2">
                        <div>
                            <div className="fw-semibold">{product.name}</div>
                            <div className="text-body-secondary small">{format(messages.edition, { edition: price.edition })}</div>
                        </div>
                        <div className="fs-5 fw-semibold">{formatPrice(price.amount, price.currency)}</div>
                    </Card.Body>
                </Card>

                {!user && (
                    <Field className="mb-4" controlId="checkout-email" label={messages.email} error={(validated && problems.email) || fieldError(error, 'email')}
                           help={rich(messages.emailHelp, { link: text => <Link to={`/login?returnTo=${encodeURIComponent(`/checkout/${sku}`)}`}>{text}</Link> })}>
                        <FieldControl type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} disabled={busy}/>
                    </Field>
                )}

                <Field className="mb-4" controlId="checkout-licensee" label={messages.licensee} error={(validated && problems.licensee) || fieldError(error, 'licensee')}
                       help={format(messages.licenseeHelp, { product: product.name })}>
                    <FieldControl value={licensee} onChange={e => setLicensee(e.target.value)} maxLength={LICENSEE_MAX_LENGTH} disabled={busy}/>
                </Field>

                <fieldset className="mb-4">
                    <legend className="fs-6 fw-semibold">{messages.payWith}</legend>
                    {providers.map(x => (
                        <Form.Check key={x} type="radio" id={`checkout-provider-${x}`} name="provider" label={messages.providers[x]}
                                    checked={chosen === x} onChange={() => setProvider(x)} disabled={busy}/>
                    ))}
                </fieldset>

                {chosen === 'paypal' ? (
                    <div className="mb-4">
                        <p className="small text-body-secondary">{messages.taxDepends}</p>
                        <div className="d-flex flex-wrap gap-3">
                            <Field className="flex-grow-1" controlId="checkout-country" label={messages.country} error={(validated && problems.country) || fieldError(error, 'country')}>
                                <FieldSelect value={country} onChange={e => setCountry(e.target.value)} disabled={busy} autoComplete="country">
                                    {!country && <option value="">{messages.chooseCountry}</option>}
                                    {allCountries().map(x => <option key={x.code} value={x.code}>{x.name}</option>)}
                                </FieldSelect>
                            </Field>
                            <Field controlId="checkout-postal-code" label={needsPostalCode ? messages.postalCodeRequired : messages.postalCodeOptional} error={(validated && problems.postalCode) || fieldError(error, 'postalCode')}>
                                <FieldControl value={postalCode} onChange={e => setPostalCode(e.target.value)} maxLength={16} disabled={busy} autoComplete="postal-code"/>
                            </Field>
                        </div>
                        <ErrorAlert error={quoteError}/>
                        {quote && (
                            <dl className="checkout-total row mt-3 mb-0" aria-live="polite">
                                <dt className="col-6 fw-normal">{messages.price}</dt><dd className="col-6 text-end mb-1">{formatPrice(quote.subtotal, quote.currency)}</dd>
                                <dt className="col-6 fw-normal">{messages.tax}</dt><dd className="col-6 text-end mb-1">{formatPrice(quote.tax, quote.currency)}</dd>
                                <dt className="col-6">{messages.total}</dt><dd className="col-6 text-end fw-semibold mb-0">{formatPrice(quote.total, quote.currency)}</dd>
                            </dl>
                        )}
                    </div>
                ) : (
                    <p className="small text-body-secondary mb-4">{messages.taxOnNextPage}</p>
                )}

                <Form.Group className="mb-4" controlId="checkout-agree">
                    <Form.Check>
                        <Form.Check.Input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} disabled={busy}
                                          isInvalid={validated && !!problems.agreed} aria-describedby={validated && problems.agreed ? 'checkout-agree-error' : undefined}/>
                        <Form.Check.Label>
                            {rich(messages.agree, { link: text => <Link to="/terms" target="_blank">{text}</Link>, refunds: text => <Link to="/refunds" target="_blank">{text}</Link> }, { product: product.name })}
                        </Form.Check.Label>
                        {validated && problems.agreed && <Form.Control.Feedback type="invalid" id="checkout-agree-error">{problems.agreed}</Form.Control.Feedback>}
                    </Form.Check>
                </Form.Group>

                {/* EU and UK consumers can cancel a digital purchase within 14 days, unless they expressly agree to lose
                    that right once it's delivered. */}
                <Form.Group className="mb-4" controlId="checkout-waive">
                    <Form.Check>
                        <Form.Check.Input type="checkbox" checked={waived} onChange={e => setWaived(e.target.checked)} disabled={busy}
                                          isInvalid={validated && !!problems.waived} aria-describedby={validated && problems.waived ? 'checkout-waive-error' : undefined}/>
                        <Form.Check.Label>
                            {format(messages.waive, { product: product.name })}
                        </Form.Check.Label>
                        {validated && problems.waived && <Form.Control.Feedback type="invalid" id="checkout-waive-error">{problems.waived}</Form.Control.Feedback>}
                    </Form.Check>
                </Form.Group>

                <SubmitButton busy={busy}>{chosen === 'paypal' ? messages.continueToPayPal : messages.continueToPayment}</SubmitButton>
                <p className="small text-body-secondary mt-3 mb-0">
                    <i className="bi bi-lock-fill me-1" aria-hidden="true"></i>
                    {chosen === 'paypal' ? messages.securePayPal : chosen === 'paddle' ? messages.securePaddle : messages.secureStripe}
                </p>
            </Form>
            <TrademarkNotices marks={trademarksIn(product.description)} className="mt-5"/>
        </>
    );
}
